// app/contexts/LoadingContext.tsx
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

interface LoadingContextType {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
  setLoading: (loading: boolean) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export function useLoading() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading 必须在 LoadingProvider 内使用');
  }
  return context;
}

interface LoadingProviderProps {
  children: ReactNode;
}

// 🔥 关键：避免在 SSR 和客户端渲染不同的初始状态
function LoadingProviderInner({ children }: LoadingProviderProps) {
  const [isLoading, setIsLoading] = useState(false);

  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const value = {
    isLoading,
    startLoading,
    stopLoading,
    setLoading,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
}

// 🔥 包装器组件，确保 SSR 一致性
export default function LoadingProvider({ children }: LoadingProviderProps) {
  // 服务器端和客户端都渲染相同的结构
  return <LoadingProviderInner>{children}</LoadingProviderInner>;
}